/**
 * handoffSourceVocabulary — the one shared vocabulary for `technical_trace.handoff.from`, the field
 * that records which end of a conversation initiated a handoff.
 *
 * It lives here rather than in either reader because two surfaces word the same token. The Handoff
 * Center projects it through `handoffCenterModel.handoffEventsForConversation` into a card's source
 * label; `ConversationPanel`'s collapsed trace box prints it in the "ניתוב" row. The Center mapped
 * it and the trace box did not, so the same handoff read `ניתוב המנהל` on one screen and `manager`
 * on the other — an English token inside an otherwise Hebrew row, on the row directly above the tool
 * and evidence-source rows `toolNameVocabulary` and `evidenceSourceVocabulary` exist to label.
 *
 * The producer's set is closed and is two tokens, not a classification: `conversation_service.py`
 * writes `handoff` from exactly two call sites — `_handle_ask_manager_and_route` ("manager") and
 * `handoff_conversation` ("operator"). `handoffSourceVocabulary.test.jsx` reads both call sites
 * rather than a copy of that set, so a third producer site reddens this file instead of silently
 * printing its token. `unknown` is not a producer token: it is the arm
 * `handoffCenterModel` already normalises a notice with no `handoff` block to, and it stays here so
 * both readers keep answering "we could not attribute this" with one word.
 */
export const HANDOFF_SOURCE_LABELS = {
  // The operator handed the conversation over themselves (handoff_conversation), so the label names
  // the reader's own act rather than a third party. Moved verbatim from handoffCenterModel: one
  // token must not read two ways depending on which screen the operator happened to open.
  operator: "יוזמתך",
  // The manager chose the employee (see toolNameVocabulary's `agent_manager_route`, the tool that
  // made the same decision). "המנהל" and not "מנהל הצוות" — the latter is EmployeeHeader's name for
  // the *participant role*, a different field, and this row is about the routing decision.
  manager: "ניתוב המנהל",
  unknown: "מקור לא ידוע",
};

/**
 * The Hebrew name of one handoff-source token, or the "unattributed" word for anything else.
 *
 * Distinct from `traceToolLabel`/`traceEvidenceSourceLabel`, which return the token itself for an
 * unmapped arm: those fields are open on their producer side, so an unseen token there is drift to
 * keep visible. This field's producer set is closed at two, and the second reader already answers an
 * unrecognised source with `unknown` — so this accessor keeps both readers saying one thing, and the
 * parity test is what makes a third producer token fail loudly rather than arrive as `unknown`.
 */
export const handoffSourceLabel = (token) =>
  HANDOFF_SOURCE_LABELS[token] ?? HANDOFF_SOURCE_LABELS.unknown;
